import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  Users, IndianRupee, Clock, AlertTriangle, TrendingUp, Search,
  Loader2, Plus, FileText, ArrowRight, Building2, Mail, X,
  CheckCircle2, XCircle, ChevronRight, Send, Calendar, Inbox,
  Shield, Activity, AlertCircle, ArrowUpCircle, Copy, GitBranch
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import CAConsentDialog from "../components/CAConsentDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (a) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0 }).format(a || 0);

const HEALTH_CONFIG = {
  healthy: { label: "Healthy", color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", dot: "bg-emerald-500", icon: CheckCircle2 },
  needs_attention: { label: "Needs Attention", color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", dot: "bg-amber-500", icon: AlertTriangle },
  critical: { label: "Critical", color: "text-red-700", bg: "bg-red-50", border: "border-red-200", dot: "bg-red-500", icon: AlertCircle },
  awaiting_docs: { label: "Awaiting Docs", color: "text-orange-700", bg: "bg-orange-50", border: "border-orange-200", dot: "bg-orange-500", icon: Inbox },
  upgrade_recommended: { label: "Upgrade Recommended", color: "text-purple-700", bg: "bg-purple-50", border: "border-purple-200", dot: "bg-purple-500", icon: ArrowUpCircle },
};

function HealthBadge({ status }) {
  const cfg = HEALTH_CONFIG[status] || HEALTH_CONFIG.healthy;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${cfg.bg} ${cfg.color} border ${cfg.border}`} data-testid={`health-badge-${status}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

export default function CADashboardPage() {
  const { user, isAuthenticated, loading: authLoading, isCA } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [docRequests, setDocRequests] = useState([]);
  const [pendingAccept, setPendingAccept] = useState(null);
  const [activeTab, setActiveTab] = useState("clients");
  const [misuseWarning, setMisuseWarning] = useState(null);
  const [pipelineData, setPipelineData] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) { navigate("/auth"); return; }
    if (isAuthenticated) fetchAll();
  }, [isAuthenticated, authLoading]);

  const fetchAll = async () => {
    try {
      const [dashRes, healthRes, mapRes, invRes, docRes, misuseRes, pipeRes] = await Promise.all([
        axios.get(`${API}/ca/dashboard`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API}/ca/dashboard/health`, { withCredentials: true }).catch(() => ({ data: { clients: [], action_center: {} } })),
        axios.get(`${API}/ca/mappings`, { withCredentials: true }).catch(() => ({ data: { mappings: [] } })),
        axios.get(`${API}/ca/pending-invites`, { withCredentials: true }).catch(() => ({ data: { invites: [] } })),
        axios.get(`${API}/ca/document-requests`, { withCredentials: true }).catch(() => ({ data: { requests: [] } })),
        axios.get(`${API}/ca/misuse-check`, { withCredentials: true }).catch(() => ({ data: { warning: false } })),
        axios.get(`${API}/ca/onboarding-pipeline`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setData(dashRes.data);
      setHealthData(healthRes.data);
      setMappings(mapRes.data.mappings || []);
      setPendingInvites(invRes.data.invites || []);
      setDocRequests(docRes.data.requests || []);
      setPipelineData(pipeRes.data);
      if (misuseRes.data.warning) {
        setMisuseWarning(misuseRes.data.message);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) { toast.error("Enter client email"); return; }
    setInviting(true);
    try {
      await axios.post(`${API}/ca/invite`, {
        email: inviteEmail.trim(),
        role_of_invitee: "client",
        permissions: ["view", "export"],
      }, { withCredentials: true });
      toast.success("Invitation sent!");
      setInviteEmail("");
      setShowInvite(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invite");
    } finally {
      setInviting(false);
    }
  };

  const handleAccept = async (mappingId) => {
    setPendingAccept(mappingId);
  };

  const performAccept = async (mappingId, consent) => {
    setPendingAccept(null);
    if (!consent) return;
    try {
      await axios.post(`${API}/ca/invite/${mappingId}/accept`, { consent: true }, { withCredentials: true });
      toast.success("Invitation accepted!");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept");
    }
  };

  const handleReject = async (mappingId) => {
    try {
      await axios.post(`${API}/ca/invite/${mappingId}/reject`, {}, { withCredentials: true });
      toast.success("Invitation rejected");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const handleRevoke = async (mappingId) => {
    if (!window.confirm("Revoke access to this client?")) return;
    try {
      await axios.post(`${API}/ca/invite/${mappingId}/revoke`, {}, { withCredentials: true });
      toast.success("Access revoked");
      fetchAll();
    } catch {
      toast.error("Failed to revoke");
    }
  };

  // Use healthData clients for the list (sorted by priority)
  const clientList = healthData?.clients || data?.clients || [];

  const filteredClients = useMemo(() => {
    if (!clientList.length) return [];
    if (!search.trim()) return clientList;
    const q = search.toLowerCase();
    return clientList.filter(c =>
      c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q)
    );
  }, [clientList, search]);

  if (loading || authLoading) {
    return <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#7C3AED]" /></div>;
  }

  const d = data || {};
  const ac = healthData?.action_center || {};
  const proId = user?.professional_id;

  const statCards = [
    { label: "Active Clients", value: d.total_clients || 0, icon: Users, color: "text-violet-600", bg: "bg-violet-50" },
    { label: "Total Revenue", value: fmt(d.total_revenue), icon: TrendingUp, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Net GST Payable", value: fmt(d.total_gst_payable), icon: IndianRupee, color: "text-blue-600", bg: "bg-blue-50" },
    { label: "Pending Invoices", value: d.pending_invoices || 0, icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
    { label: "Overdue", value: d.overdue_count || 0, icon: AlertTriangle, color: d.overdue_count > 0 ? "text-red-600" : "text-slate-400", bg: d.overdue_count > 0 ? "bg-red-50" : "bg-slate-50" },
  ];

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-6 px-4" data-testid="ca-dashboard-page">
      <div className="max-w-7xl mx-auto">
        {/* Misuse Warning */}
        {misuseWarning && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5 flex items-start gap-3"
            data-testid="misuse-warning"
          >
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-800">Self-Management Detected</p>
              <p className="text-xs text-amber-700 mt-0.5">{misuseWarning}</p>
            </div>
            <Button size="sm" variant="outline" className="text-xs border-amber-300 text-amber-700 hover:bg-amber-100" onClick={() => setMisuseWarning(null)}>Dismiss</Button>
          </motion.div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="ca-welcome">
              CA/Professional Dashboard
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <p className="text-sm text-slate-500">Welcome back, {user?.name?.split(" ")[0] || "Professional"}</p>
              {proId && (
                <button
                  onClick={() => { navigator.clipboard.writeText(proId); toast.success("Professional ID copied!"); }}
                  className="inline-flex items-center gap-1 text-xs font-mono font-semibold bg-violet-50 text-violet-700 px-2 py-0.5 rounded border border-violet-100 hover:bg-violet-100 transition-colors cursor-pointer"
                  data-testid="pro-id-copy"
                >
                  {proId} <Copy className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {proId && (
              <Link to={`/pro/${proId}`} className="text-xs text-violet-600 hover:text-violet-800 font-medium underline underline-offset-2" data-testid="view-public-profile-link">
                View Public Profile
              </Link>
            )}
            <Button onClick={() => setShowInvite(true)} className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-sm" data-testid="invite-client-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Invite Client
            </Button>
          </div>
        </div>

        {/* Action Center */}
        {(ac.critical > 0 || ac.needs_attention > 0 || ac.awaiting_docs > 0) && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 mb-6"
            data-testid="action-center"
          >
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-5 h-5 text-[#7C3AED]" />
              <h2 className="text-base font-bold text-slate-900">Action Center</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {ac.critical > 0 && (
                <div className="flex items-center gap-3 p-3 bg-red-50 rounded-xl border border-red-100" data-testid="action-critical">
                  <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-red-700">{ac.critical}</p>
                    <p className="text-[10px] font-medium text-red-500 uppercase tracking-wider">Critical</p>
                  </div>
                </div>
              )}
              {ac.needs_attention > 0 && (
                <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl border border-amber-100" data-testid="action-needs-attention">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-amber-700">{ac.needs_attention}</p>
                    <p className="text-[10px] font-medium text-amber-500 uppercase tracking-wider">Needs Attention</p>
                  </div>
                </div>
              )}
              {ac.awaiting_docs > 0 && (
                <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-xl border border-orange-100" data-testid="action-awaiting-docs">
                  <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                    <Inbox className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-orange-700">{ac.awaiting_docs}</p>
                    <p className="text-[10px] font-medium text-orange-500 uppercase tracking-wider">Awaiting Docs</p>
                  </div>
                </div>
              )}
              {ac.upgrade_recommended > 0 && (
                <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-xl border border-purple-100" data-testid="action-upgrade">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <ArrowUpCircle className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-purple-700">{ac.upgrade_recommended}</p>
                    <p className="text-[10px] font-medium text-purple-500 uppercase tracking-wider">Upgrade</p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6" data-testid="ca-stats">
          {statCards.map((card, i) => (
            <motion.div key={card.label} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              className="bg-white rounded-xl border border-slate-100 shadow-sm p-4" data-testid={`ca-stat-${card.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-8 h-8 rounded-lg ${card.bg} flex items-center justify-center`}>
                  <card.icon className={`w-4 h-4 ${card.color}`} />
                </div>
                <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">{card.label}</span>
              </div>
              <p className="text-xl font-bold text-slate-900">{card.value}</p>
            </motion.div>
          ))}
        </div>

        {/* Pending Invites */}
        {pendingInvites.length > 0 && (
          <div className="bg-violet-50 border border-violet-100 rounded-xl p-4 mb-6" data-testid="pending-invites-section">
            <h3 className="text-sm font-semibold text-violet-800 mb-3">Pending Invitations ({pendingInvites.length})</h3>
            <div className="space-y-2">
              {pendingInvites.map(inv => (
                <div key={inv.mapping_id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-violet-100">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{inv.client_name || inv.client_email}</p>
                    <p className="text-xs text-slate-500">{inv.client_email}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => handleAccept(inv.mapping_id)} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid={`accept-${inv.mapping_id}`}>
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Accept
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleReject(inv.mapping_id)} className="text-xs border-red-200 text-red-600" data-testid={`reject-${inv.mapping_id}`}>
                      <XCircle className="w-3 h-3 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1 mb-4 w-fit" data-testid="ca-tabs">
          {[
            { id: "clients", label: "Clients", icon: Users },
            { id: "pipeline", label: "Onboarding", icon: GitBranch },
            { id: "requests", label: "Doc Requests", icon: Inbox },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`} data-testid={`tab-${tab.id}`}>
              <tab.icon className="w-3.5 h-3.5" /> {tab.label}
            </button>
          ))}
        </div>

        {/* Client List */}
        {activeTab === "clients" && (
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="client-list">
            <div className="flex items-center gap-3 p-4 border-b border-slate-100">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Search clients..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 text-sm" data-testid="ca-client-search" />
              </div>
              <span className="text-xs text-slate-400">{filteredClients.length} client{filteredClients.length !== 1 ? "s" : ""}</span>
            </div>

            {filteredClients.length > 0 ? (
              <div className="divide-y divide-slate-50">
                {filteredClients.map(client => (
                  <Link key={client.client_user_id} to={`/ca/clients/${client.client_user_id}`}
                    className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors group" data-testid={`ca-client-row-${client.client_user_id}`}>
                    <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center text-violet-600 font-bold text-sm flex-shrink-0">
                      {client.name?.charAt(0) || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-slate-900 truncate">{client.name}</p>
                        <HealthBadge status={client.health_status} />
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {client.email} &middot; {client.invoice_count} invoices
                        {client.health_reasons?.length > 0 && client.health_status !== "healthy" && (
                          <span className="text-slate-400"> &middot; {client.health_reasons[0]}</span>
                        )}
                      </p>
                    </div>
                    <div className="text-right hidden sm:block">
                      <p className="text-sm font-bold text-slate-900">{fmt(client.revenue || client.receivable)}</p>
                      <p className="text-xs text-slate-500">{client.pending_count} pending</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors" />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h3 className="text-base font-semibold text-slate-900 mb-1">{search ? "No matching clients" : "No clients yet"}</h3>
                <p className="text-sm text-slate-500 mb-4">{search ? "Try a different search term" : "Invite your first client to get started"}</p>
                {!search && (
                  <Button onClick={() => setShowInvite(true)} className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-sm">
                    <Plus className="w-4 h-4 mr-1" /> Invite Client
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Onboarding Pipeline Tab */}
        {activeTab === "pipeline" && pipelineData && (
          <div className="space-y-4" data-testid="onboarding-pipeline">
            {/* Pipeline Summary */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { stage: "invited", label: "Invited", color: "amber", count: pipelineData.counts?.invited || 0 },
                { stage: "pending", label: "Pending Setup", color: "blue", count: pipelineData.counts?.pending || 0 },
                { stage: "active", label: "Active", color: "emerald", count: pipelineData.counts?.active || 0 },
              ].map(s => (
                <div key={s.stage} className={`bg-${s.color}-50 border border-${s.color}-200 rounded-xl p-4 text-center`} data-testid={`pipeline-stage-${s.stage}`}>
                  <p className={`text-2xl font-bold text-${s.color}-700`}>{s.count}</p>
                  <p className={`text-xs font-medium text-${s.color}-600 mt-0.5`}>{s.label}</p>
                </div>
              ))}
            </div>

            {/* Plan Distribution */}
            {pipelineData.plan_summary && (
              <div className="bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-4" data-testid="plan-distribution">
                <span className="text-xs font-medium text-slate-500">Client Plans:</span>
                <span className="text-xs font-semibold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">{pipelineData.plan_summary.free_plan || 0} Free</span>
                <span className="text-xs font-semibold text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full">{pipelineData.plan_summary.trial || 0} Trial</span>
                <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">{pipelineData.plan_summary.paid_plan || 0} Paid</span>
              </div>
            )}

            {/* Pipeline Columns */}
            {["invited", "pending", "active"].map(stage => {
              const clients = pipelineData.pipeline?.[stage] || [];
              if (clients.length === 0) return null;
              const cfg = {
                invited: { label: "Invited", desc: "Awaiting response", color: "amber" },
                pending: { label: "Pending Setup", desc: "Accepted, company setup needed", color: "blue" },
                active: { label: "Active Clients", desc: "Fully onboarded", color: "emerald" },
              }[stage];
              return (
                <div key={stage} className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid={`pipeline-col-${stage}`}>
                  <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">{cfg.label} ({clients.length})</h3>
                      <p className="text-xs text-slate-500">{cfg.desc}</p>
                    </div>
                    <span className={`w-2.5 h-2.5 rounded-full bg-${cfg.color}-500`} />
                  </div>
                  <div className="divide-y divide-slate-50">
                    {clients.map(c => (
                      <div key={c.mapping_id} className="flex items-center gap-3 p-3 hover:bg-slate-50" data-testid={`pipeline-client-${c.mapping_id}`}>
                        <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-sm flex-shrink-0">
                          {(c.client_name || c.client_email || "?").charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{c.client_name || c.client_email}</p>
                          <p className="text-xs text-slate-500">
                            {c.has_company ? `${c.invoice_count} invoices` : "No company set up"}
                            {c.plan_stage && (
                              <span className={`ml-1.5 font-medium ${
                                c.plan_stage === "paid_plan" ? "text-emerald-600" :
                                c.plan_stage === "trial" ? "text-violet-600" : "text-slate-400"
                              }`}>
                                &middot; {c.plan_stage === "paid_plan" ? "Paid" : c.plan_stage === "trial" ? "Trial" : "Free"}
                              </span>
                            )}
                          </p>
                        </div>
                        {stage === "active" && c.client_user_id && (
                          <Link to={`/ca/clients/${c.client_user_id}`} className="text-xs text-violet-600 hover:underline font-medium">View</Link>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Document Requests Tab */}
        {activeTab === "requests" && (
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="doc-requests-list">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900">Document Requests</h3>
            </div>
            {docRequests.length > 0 ? (
              <div className="divide-y divide-slate-50">
                {docRequests.map(req => (
                  <div key={req.request_id} className="p-4 flex items-center gap-4" data-testid={`doc-req-${req.request_id}`}>
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                      req.status === "completed" ? "bg-emerald-50 text-emerald-600" : req.status === "partially_uploaded" ? "bg-amber-50 text-amber-600" : "bg-slate-100 text-slate-500"
                    }`}>
                      <Calendar className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">{req.title}</p>
                      <p className="text-xs text-slate-500">For: {req.client_name || req.client_user_id} &middot; {req.month || "N/A"}</p>
                    </div>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      req.status === "completed" ? "bg-emerald-50 text-emerald-700" : req.status === "partially_uploaded" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"
                    }`}>{req.status.replace("_", " ")}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-slate-400">
                <Inbox className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">No document requests yet</p>
              </div>
            )}
          </div>
        )}

        {/* Connection History */}
        {mappings.filter(m => m.status === "invited").length > 0 && (
          <div className="mt-6 bg-white rounded-xl border border-slate-100 shadow-sm p-4" data-testid="sent-invites">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Sent Invitations (Awaiting Response)</h3>
            <div className="space-y-2">
              {mappings.filter(m => m.status === "invited" && m.invited_by === (isCA ? "ca" : "client")).map(m => (
                <div key={m.mapping_id} className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                  <div>
                    <p className="text-sm font-medium text-slate-700">{m.client_name || m.client_email || m.ca_name || m.ca_email}</p>
                    <p className="text-xs text-slate-400">Invited {new Date(m.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">Pending</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Invite Modal */}
      <AnimatePresence>
        {showInvite && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="invite-modal">
              <div className="flex items-center justify-between p-5 border-b border-slate-100">
                <h2 className="text-lg font-bold text-slate-900">Invite Client</h2>
                <button onClick={() => setShowInvite(false)} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Client's Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="client@business.com" className="pl-9" data-testid="invite-email-input" />
                  </div>
                  <p className="text-xs text-slate-400 mt-1.5">They'll receive a notification to accept your connection.</p>
                </div>
              </div>
              <div className="p-5 border-t border-slate-100 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowInvite(false)}>Cancel</Button>
                <Button onClick={handleInvite} disabled={inviting} className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white" data-testid="send-invite-btn">
                  {inviting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />} Send Invite
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <CAConsentDialog
        open={!!pendingAccept}
        onClose={() => setPendingAccept(null)}
        onConfirm={(consent) => performAccept(pendingAccept, consent)}
        variant="ca-accept"
        inviterName={(pendingInvites.find(i => i.mapping_id === pendingAccept) || {}).client_name || "this client"}
      />
    </div>
  );
}
